import { Injectable } from '@angular/core';
import { AngularFireAnalytics } from '@angular/fire/compat/analytics';
import { LanguageService } from './language.service';

/**
 * The single place this app talks to Firebase Analytics.
 *
 * Every event used to hand-copy its own base bundle across 7 files and ~34 call sites. That
 * duplication is what let a bug live unnoticed for months: the bundle sent `client_id` and `source`,
 * and **both are reserved field names on GA4's web (gtag) transport**. gtag consumes them as
 * configuration — `client_id` overrides the GA client id, `source` feeds traffic-source attribution
 * — instead of recording custom event parameters. Because every shop-scoped dashboard query filters
 * on `customEvent:client_id`, the web app's traffic matched nothing and vanished from *every*
 * per-shop chart: menu opens, the funnel, top products, banner engagement. The Traffic Sources donut
 * reading "100% iOS" was only the visible corner of it.
 *
 * So on web the shop travels as `venue_id`, a name gtag does not reserve. iOS and Android send
 * `venue_id` **and** `client_id` (they have builds in the wild that only know the old name), and the
 * dashboard matches on either.
 *
 * **`source` had the identical problem and was left behind by that fix.** It is reserved *and* it is
 * sent as a number, so `customEvent:source` reads `(not set)` for every web event — which is why the
 * dashboard's per-client split of touristic notifications rendered "Web 6" underneath a step reading
 * 640 sends. It now travels as `client_source`, a string, under a name gtag ignores. Both names are
 * sent: `source` still feeds GA4's device report, which the Traffic Sources donut is built on.
 *
 * **Do not "clean up" the apparent duplication of `source` / `client_source` or `client_id` /
 * `venue_id`.** Each pair exists because one half is consumed by the transport and the other half is
 * what actually reaches a report, and in both cases the redundancy was added *after* the missing
 * data was traced back to its absence.
 *
 * Two rules for anyone adding a parameter here:
 *  - check the name against gtag's reserved list first — `page_*`, `language`, `session_id`,
 *    `user_id`, `medium`, `campaign`, `term`, `content` and the `firebase_`/`google_`/`ga_`
 *    prefixes are all taken. That is why the language is `app_language` and the paging parameter
 *    is `list_page`.
 *  - send ids as strings. A parameter that is a number on one event and a string on another
 *    registers as a metric, and its custom dimension then reads `(not set)` everywhere.
 */
@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  /** 3 = Web. 1 = iOS, 2 = Android, 4 = App Clip, 6 = TV. */
  private static readonly SOURCE = 3;
  private static readonly PLATFORM = 3;

  constructor(
    private analytics: AngularFireAnalytics,
    private languageService: LanguageService
  ) {}

  /**
   * Log an event with the base bundle merged in.
   *
   * `params` wins over the base bundle, so an event can pass an explicit `app_language`
   * (`language_changed` does, to avoid depending on whether it fires before or after the switch).
   */
  log(eventName: string, params: Record<string, any> = {}): void {
    this.analytics.logEvent(eventName, { ...this.baseParams(), ...this.clean(params) });
  }

  /** Sets the user-scoped `shop_id` property, which drives the dashboard's realtime widget. */
  setShop(shopId: string | null | undefined): void {
    this.analytics.setUserProperties({ shop_id: this.str(shopId) });
  }

  private baseParams(): Record<string, any> {
    const params: Record<string, any> = {
      source: AnalyticsService.SOURCE,
      platform: AnalyticsService.PLATFORM,
      // The SAME value as `source`, under a name gtag does not eat and as a STRING. Both halves of
      // that sentence are load-bearing, and this is the `client_id` -> `venue_id` fix above applied
      // to the one parameter it left behind. @see the class note for the full story.
      //
      // `source` is unusable as a custom dimension here for two independent reasons: gtag consumes
      // the name for traffic-source attribution, and a numeric value is encoded as `epn.*` (a
      // metric) rather than `ep.*` (a dimension). Either alone makes `customEvent:source` read
      // `(not set)`. The dashboard's per-client split of touristic notifications was showing "Web 6"
      // against 640 sends because of it.
      //
      // ⚠️ `source` is KEPT, not replaced. GA4's device report falls back to the built-in `platform`
      // when `source` is absent, and the city page's Traffic Sources donut is built on that column —
      // removing it would break a working chart to fix a broken one. iOS and Android keep sending
      // only `source`; they go through the Firebase SDK, not gtag, where it resolves correctly.
      client_source: String(AnalyticsService.SOURCE)
    };

    const deviceId = localStorage.getItem('deviceGuid');
    if (deviceId) {
      params['device_id'] = deviceId;
    }

    // The shop, under a name gtag does not eat. Deliberately NOT `client_id` — see the class note.
    const venueId = sessionStorage.getItem('clientId');
    if (venueId) {
      params['venue_id'] = String(venueId);
    }

    // The table, on every event and resolved by ONE rule shared verbatim with iOS and Android:
    // the legacy `tableId` when the QR carried one, otherwise `tableIdentifier`.
    //
    // ⚠️ THE RULE, NOT JUST THE NAME, IS THE CONTRACT. A venue's QRs are one style or the other,
    // and `tableIdentifier` is the `id` FIELD of the shop's `tables` document — the value
    // `getTableRegionInfo` queries on, the one iOS fetches its table document by, and the one
    // Android resolves into its `TableResponse`. Both ids therefore mean "this table" on all three
    // clients, and only this ordering makes `table_id` joinable across them.
    //
    // ⚠️ NOT the table's display NAME. iOS used to send `currentTableName` on `order_created`,
    // so one order reported "Table 4" from an iPhone and an id from everywhere else — one
    // dashboard query, three vocabularies, nothing that could be merged.
    //
    // Deliberately NOT overridden per event any more. Nine order-flow events used to hand-copy
    // `table_id`, which is how the platforms drifted in the first place; the value now exists in
    // exactly one place per client. `||` rather than `??` on purpose: `''` must fall through to
    // the identifier, not win as a value.
    //
    // Empty is omitted rather than sent as `''` — an empty string is a real dimension value to
    // GA4, not `(not set)`, and would file every delivery and pickup visit under one phantom table.
    const tableId = sessionStorage.getItem('tableId') || sessionStorage.getItem('tableIdentifier');
    if (tableId) {
      params['table_id'] = String(tableId);
    }

    const language = this.languageService.getCurrentLang();
    if (language) {
      params['app_language'] = language;
    }

    return params;
  }

  /**
   * Drops empty values and coerces ids to strings.
   *
   * Empty is omitted rather than sent as `''`, because an empty string is a real dimension value to
   * GA4 — not `(not set)` — and collapses every id-less event into one phantom bucket.
   */
  private clean(params: Record<string, any>): Record<string, any> {
    const out: Record<string, any> = {};
    Object.keys(params).forEach(key => {
      const value = params[key];
      if (value === null || value === undefined || value === '') {
        return;
      }
      out[key] = typeof value === 'number' || typeof value === 'boolean' ? value : String(value);
    });
    return out;
  }

  private str(value: any): string {
    return value === null || value === undefined ? '' : String(value);
  }
}

/**
 * The two add-on ("supplement") events.
 *
 * They get a note here rather than a helper method — `log()` already carries the base bundle, and
 * every event in this app is a literal at its call site — because their parameter shapes are a
 * contract with iOS and Android, not a local choice. One dashboard query reads all three clients;
 * a name or a type that drifts on one of them silently drops that platform out of every add-on
 * chart, which is exactly how the `client_id` bug above went unnoticed for months.
 *
 * `supplements_changed` → `{ product_id: string, type: '<fromCount>-><toCount>' }`
 *   Emitted by `CartService.changeSupplements` when the SELECTION on a line already in the order
 *   changes. Not on a pure quantity change (nothing about the selection moved) and not on a plain
 *   add or remove — those log `product_added` / `product_removed`, and attributing an edit as both
 *   would double-count the dish in the funnel. The before/after counts are packed into one `type`
 *   string instead of two numeric parameters so a single query spans platforms; iOS builds the same
 *   `"1->2"` in `AnalyticsEvents.supplementsChanged`. The add-on ids are deliberately not sent —
 *   the dimension worth reporting is whether guests load up a plain dish or trim a loaded one, not
 *   which topping.
 *
 * `supplement_combinations_shown` → `{ product_id: string, combination_count: number }`
 *   Fired once when the product sheet opens, and ONLY when the guest already holds that dish under
 *   two or more distinct add-on selections — the case the "in your order" list exists to
 *   disambiguate. Firing it at 0 or 1 would bury the signal under every ordinary sheet open, so
 *   the count is never reported below 2. iOS gates it identically in
 *   `ProductDetailsView.setupInitialState()`.
 *
 * Types are load-bearing on both: `product_id` a STRING, `combination_count` a NUMBER, matching
 * iOS. `clean()` below passes numbers through untouched and stringifies everything else, so a
 * product id handed over as a raw `number` — or a count wrapped in `String()` — would register on
 * web as the opposite kind of custom field from the one iOS registered, and a parameter GA4 has
 * seen as both a metric and a dimension reads `(not set)` from then on.
 */

/** `entry_point` values, shared verbatim with iOS and Android. */
export const AnalyticsEntry = {
  BANNER: 'banner',
  MOSAIC: 'mosaic',
  FAB: 'fab',
  DEEPLINK: 'deeplink',
  LIST: 'list',
  HERO: 'hero',
  RECOMMENDED: 'recommended',
  FIRST: 'first',
  SWITCH: 'switch'
} as const;
